import { useState, useEffect } from 'react';
import { initializeObserver, handleTouchStart, handleTouchEnd } from './utils/scrollHandler';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useLanguage } from './context/LanguageContext';
import ModelToolMBS from './pages/ModelToolMBS';
import Lebenslauf from './pages/Lebenslauf';
import LebenslaufV2 from './pages/LebenslaufV2';
import BMSProjekte from './pages/BMSProjekte';
import Tagbar from './pages/Tagbar';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import QRTool from './pages/QRTool';
import QRRedirect from './pages/QRRedirect';
import AITool from './pages/AITool';
import PinLock from './components/PinLock';

const ScrollToSection = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    initializeObserver();
  }, []);

  useEffect(() => {
    const targetId = location.state && location.state.scrollTo;
    if (targetId) {
      setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  return children;
};

function App() {
  const { language, switchLanguage, t } = useLanguage();
  const [darkMode, setDarkMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formData, setFormData] = useState({ name: '', message: '' });
  const [formSent, setFormSent] = useState(false);
  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem('pin_unlocked') === '1'
  );

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setDarkMode(true);
      document.documentElement.setAttribute('data-theme', 'dark');
    }

    initializeObserver();

    const isDesktop = window.matchMedia('(min-width: 769px)').matches;
    if (isDesktop) {
      document.addEventListener('touchstart', handleTouchStart);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  useEffect(() => {
    const sectionIds = ['home', 'about', 'projects', 'contact'];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    document.documentElement.setAttribute('data-theme', darkMode ? 'light' : 'dark');
    localStorage.setItem('theme', darkMode ? 'light' : 'dark');
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    window.location.href = `mailto:Tavlin@hotmail.de?subject=${encodeURIComponent('Kontakt von ' + formData.name)}&body=${encodeURIComponent(formData.message)}`;
    setFormSent(true);
    setFormData({ name: '', message: '' });
  };

  const handleUnlock = () => {
    sessionStorage.setItem('pin_unlocked', '1');
    setUnlocked(true);
  };

  if (!unlocked) {
    return (
      <Router>
        <Routes>
          <Route path="/r/:shortcode" element={<QRRedirect />} />
          <Route path="*" element={<PinLock onUnlock={handleUnlock} />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/modeltool-mbs" element={<ModelToolMBS />} />
        <Route path="/lebenslauf" element={<Lebenslauf />} />
        <Route path="/lebenslauf-v2" element={<LebenslaufV2 />} />
        <Route path="/bms-projekte" element={<BMSProjekte />} />
        <Route path="/tagbar" element={<Tagbar />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
        <Route path="/qr-tool" element={<QRTool />} />
        <Route path="/ai-tool" element={<AITool />} />
        <Route path="/r/:shortcode" element={<QRRedirect />} />
        <Route path="/" element={
          <ScrollToSection>
            <div className={`App ${darkMode ? 'dark' : 'light'}`}>
              <nav className="nav-header">
                <a href="#home" className="nav-logo" onClick={closeMenu}>ET</a>
                <button
                  className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
                  onClick={toggleMenu}
                  aria-label="Toggle menu"
                >
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
                <div className={`nav-controls ${isMenuOpen ? 'active' : ''}`}>
                  <ul>
                    <li>
                      <a href="#home" className={`string-hover${activeSection === 'home' ? ' nav-link--active' : ''}`} onClick={closeMenu}>
                        {t.nav.home}
                      </a>
                    </li>
                    <li><a href="#about" className={`string-hover${activeSection === 'about' ? ' nav-link--active' : ''}`} onClick={closeMenu}>{t.nav.about}</a></li>
                    <li><a href="#projects" className={`string-hover${activeSection === 'projects' ? ' nav-link--active' : ''}`} onClick={closeMenu}>{t.nav.projects}</a></li>
                    <li><a href="#contact" className={`string-hover${activeSection === 'contact' ? ' nav-link--active' : ''}`} onClick={closeMenu}>{t.nav.contact}</a></li>
                  </ul>
                  <button className="theme-toggle string-hover" onClick={toggleTheme}>
                    {darkMode ? '☼' : '☾'}
                  </button>
                  <div className="lang-switcher">
                    {['de', 'en' /*, 'tr' */].map(lang => (
                      <button
                        key={lang}
                        className={`lang-btn${language === lang ? ' active' : ''}`}
                        onClick={() => switchLanguage(lang)}
                      >
                        {lang.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </nav>

              <section id="home" className="home-section">
                <h1 className="animated-text">Emin Tavli</h1>
                <p className="home-badge">{t.home.subtitle}</p>
                <p className="fade-in home-tagline">{t.home.tagline}</p>
                <div className="hero-actions">
                  <a href="#projects" className="hero-btn hero-btn--primary">{t.home.ctaProjects}</a>
                  <a href="#contact" className="hero-btn hero-btn--ghost">{t.home.ctaContact}</a>
                </div>
                <div className="hero-socials">
                  <a href="https://github.com/Teflonius" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 .5C5.73.5.5 5.73.5 12c0 5.08 3.29 9.39 7.86 10.91.57.1.78-.25.78-.55 0-.27-.01-1.17-.02-2.12-3.2.7-3.88-1.36-3.88-1.36-.52-1.33-1.28-1.69-1.28-1.69-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.03 1.76 2.7 1.25 3.36.96.1-.75.4-1.25.73-1.54-2.55-.29-5.23-1.28-5.23-5.68 0-1.26.45-2.28 1.18-3.09-.12-.29-.51-1.47.11-3.06 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.79 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.24 2.77.12 3.06.74.81 1.18 1.83 1.18 3.09 0 4.41-2.69 5.38-5.25 5.67.41.36.78 1.06.78 2.14 0 1.55-.01 2.79-.01 3.17 0 .3.2.66.79.55A10.53 10.53 0 0 0 23.5 12C23.5 5.73 18.27.5 12 .5Z" />
                    </svg>
                  </a>
                  <a href="mailto:Tavlin@hotmail.de" aria-label="Email">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
                      <path d="m3.5 6 8.5 7 8.5-7" />
                    </svg>
                  </a>
                </div>
              </section>

              <section id="about" className="about-section">
                <h2>{t.about.title}</h2>
                <div className="about-stats">
                  <div className="about-stat">
                    <span className="about-stat__number">5+</span>
                    <span className="about-stat__label">{t.about.statYears}</span>
                  </div>
                  <div className="about-stat">
                    <span className="about-stat__number">E2E</span>
                    <span className="about-stat__label">{t.about.statProjects}</span>
                  </div>
                  <div className="about-stat">
                    <span className="about-stat__number">CI/CD</span>
                    <span className="about-stat__label">{t.about.statTypo3}</span>
                  </div>
                  <div className="about-stat">
                    <span className="about-stat__number">SWA</span>
                    <span className="about-stat__label">{t.about.statFullstack}</span>
                  </div>
                </div>
                <div className="about-focus">
                  <h3>{t.about.focusTitle}</h3>
                  <ul>
                    <li>{t.about.focus1}</li>
                    <li>{t.about.focus2}</li>
                    <li>{t.about.focus3}</li>
                    <li>{t.about.focus4}</li>
                    <li>{t.about.focus5}</li>
                    <li>{t.about.focus6}</li>
                    <li>{t.about.focus7}</li>
                  </ul>
                </div>
                <div className="about-stack">
                  {['React', 'Vue', 'Node.js', 'PHP', 'TypeScript', 'MySQL', 'TYPO3', 'WordPress', 'WooCommerce', 'Shopify', 'Docker', 'Azure', 'Git', 'Plesk'].map(tech => (
                    <span key={tech} className="about-badge">{tech}</span>
                  ))}
                </div>
              </section>

              <section id="projects" className="projects-section">
                <h2>{t.projects.title}</h2>
                <div className="projects-grid">
                  <div className="project-card project-card--modeltool">
                    <h3>Modeltool</h3>
                    <p>{t.projects.modeltool.desc}</p>
                    <div className="project-links">
                      <Link to="/modeltool-mbs" state={{ scrollTo: 'projects' }}>
                        {t.projects.modeltool.link}
                      </Link>
                    </div>
                  </div>
                  <div className="project-card project-card--bms">
                    <h3>BMS & Bertelsmann</h3>
                    <p>{t.projects.bms.desc}</p>
                    <div className="project-links">
                      <Link to="/bms-projekte" state={{ scrollTo: 'projects' }}>
                        {t.projects.modeltool.link}
                      </Link>
                    </div>
                  </div>
                  <div className="project-card project-card--tagbar">
                    <h3>Tagbar</h3>
                    <p>{t.projects.tagbar.desc}</p>
                    <div className="project-links">
                      <Link to="/tagbar" state={{ scrollTo: 'projects' }}>
                        {t.projects.tagbar.link}
                      </Link>
                    </div>
                  </div>
                  <div className="project-card project-card--qrtool">
                    <h3>QR Tool</h3>
                    <p>{t.projects.qrtool.desc}</p>
                    <div className="project-links">
                      <Link to="/qr-tool" state={{ scrollTo: 'projects' }}>
                        {t.projects.qrtool.link}
                      </Link>
                    </div>
                  </div>
                  <div className="project-card project-card--aitool">
                    <h3>Job Fit Analyzer</h3>
                    <p>{t.projects.aitool.desc}</p>
                    <div className="project-links">
                      <Link to="/ai-tool" state={{ scrollTo: 'projects' }}>
                        {t.projects.aitool.link}
                      </Link>
                    </div>
                  </div>
                </div>
              </section>

              <section id="contact" className="contact-section">
                <h2>{t.contact.title}</h2>
                <p>{t.contact.text}</p>
                <form className="contact-form" onSubmit={handleFormSubmit}>
                  <input
                    type="text"
                    placeholder={t.contact.formName}
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                  <textarea
                    placeholder={t.contact.formMessage}
                    value={formData.message}
                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    required
                  />
                  <button type="submit">{formSent ? t.contact.formSent : t.contact.formSend}</button>
                </form>
                <div className="contact-links">
                  <a href="mailto:Tavlin@hotmail.de" className="swing-animation">
                    {t.contact.email}
                  </a>
                  <a href="https://github.com/Teflonius" target="_blank" rel="noopener noreferrer">
                    GitHub
                  </a>
                </div>
                <div className="legal-links">
                  <Link to="/impressum">Impressum</Link>
                  <span>·</span>
                  <Link to="/datenschutz">Datenschutz</Link>
                </div>
              </section>
            </div>
          </ScrollToSection>
        } />
      </Routes>
    </Router>
  );
}

export default App;
